import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Application, ApplicationDecision } from '@regigov/contracts';
import { applicationsApi } from '../../api/participants';
import { DefinitionList, ProblemAlert, StatusBadge, formatDateTime } from '../../components/ui';

export function ApplicationDetail() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const app = useQuery({ queryKey: ['applications', 'detail', id], queryFn: () => applicationsApi.get(id) });

  function onUpdated(updated: Application) {
    qc.setQueryData(['applications', 'detail', id], updated);
    void qc.invalidateQueries({ queryKey: ['applications'] });
    void qc.invalidateQueries({ queryKey: ['participants'] });
  }

  if (app.isLoading) return <p className="muted">Loading…</p>;
  if (app.error || !app.data) return <ProblemAlert error={app.error ?? new Error('Not found')} />;
  const a = app.data;
  const p = a.participant;
  const decidable = a.status === 'submitted' || a.status === 'under_assessment';

  return (
    <>
      <p className="crumbs">
        <Link to="/cer/participants">Participants</Link> / <Link to="/cer/participants/applications">Applications</Link> / {p.legalName}
      </p>
      <div className="row">
        <h1>{p.legalName}</h1>
        <StatusBadge status={a.status} />
      </div>

      <div className="cards">
        <section className="card">
          <h2>Application</h2>
          <DefinitionList
            items={[
              ['Application ID', <code key="a">{a.id}</code>],
              ['Participant ID', <Link key="p" to={`/cer/participants/${p.id}`}><code>{p.id}</code></Link>],
              ['Submitted', formatDateTime(a.submittedAt)],
              ['Assessor', a.assessorName],
              ['Decided', formatDateTime(a.decidedAt)],
              ['Decision', a.decision],
              ['Decision reason', a.decisionReason],
              ['Authorised to apply', a.declarations.authorisedToApply ? 'Declared' : 'Not declared'],
              ['Information true', a.declarations.informationTrue ? 'Declared' : 'Not declared'],
              ['Evidence notes', a.declarations.evidenceNotes],
            ]}
          />
        </section>
        <section className="card">
          <h2>Organisation</h2>
          <DefinitionList
            items={[
              ['Legal name', p.legalName],
              ['Trading name', p.tradingName],
              ['ABN', p.abn],
              ['Type', p.participantType],
              ['Contact', `${p.contactName} · ${p.contactEmail} · ${p.contactPhone}`],
              ['Address', `${p.address.line1}${p.address.line2 ? `, ${p.address.line2}` : ''}, ${p.address.suburb} ${p.address.state} ${p.address.postcode}`],
              ['Record version', p.version],
            ]}
          />
        </section>
      </div>

      <section className="card">
        <h2>Assessment notes</h2>
        {a.notes.length === 0 && <p className="muted">No notes yet.</p>}
        <ul className="notes">
          {a.notes.map((n) => (
            <li key={n.id}>
              <span className="muted">
                {formatDateTime(n.createdAt)} · {n.kind} · {n.authorName ?? 'system'}
              </span>
              <p>{n.body}</p>
            </li>
          ))}
        </ul>
        {decidable && <NoteForm id={a.id} onUpdated={onUpdated} />}
      </section>

      {decidable && <DecisionForm id={a.id} onUpdated={onUpdated} />}
    </>
  );
}

function NoteForm({ id, onUpdated }: { id: string; onUpdated: (a: Application) => void }) {
  const [body, setBody] = useState('');
  const note = useMutation({
    mutationFn: () => applicationsApi.addNote(id, body),
    onSuccess: (a) => {
      setBody('');
      onUpdated(a);
    },
  });
  function onSubmit(e: FormEvent) {
    e.preventDefault();
    note.mutate();
  }
  return (
    <form className="form" onSubmit={onSubmit}>
      <label>
        Add assessment note
        <textarea name="note" value={body} onChange={(e) => setBody(e.target.value)} rows={2} required maxLength={4000} />
      </label>
      <ProblemAlert error={note.error} />
      <div>
        <button type="submit" disabled={note.isPending || !body.trim()}>
          Add note
        </button>
      </div>
    </form>
  );
}

function DecisionForm({ id, onUpdated }: { id: string; onUpdated: (a: Application) => void }) {
  const [decision, setDecision] = useState<ApplicationDecision['decision']>('approve');
  const [reason, setReason] = useState('');
  const decide = useMutation({ mutationFn: () => applicationsApi.decide(id, { decision, reason }), onSuccess: onUpdated });
  function onSubmit(e: FormEvent) {
    e.preventDefault();
    decide.mutate();
  }
  return (
    <form className="form card" onSubmit={onSubmit}>
      <h2>Decision</h2>
      <div className="row">
        {(['approve', 'return', 'reject'] as const).map((d) => (
          <label key={d} className="check">
            <input type="radio" name="decision" value={d} checked={decision === d} onChange={() => setDecision(d)} />
            {d === 'approve' ? 'Approve' : d === 'return' ? 'Return for correction' : 'Reject'}
          </label>
        ))}
      </div>
      <label>
        Reason (recorded on the application and in the audit trail)
        <textarea name="reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} required minLength={10} maxLength={2000} />
      </label>
      <ProblemAlert error={decide.error} />
      <div>
        <button type="submit" disabled={decide.isPending || reason.trim().length < 10}>
          Record decision
        </button>
      </div>
    </form>
  );
}
